import { useEffect, useState } from "react";
import { DEFAULT_SIGMA_FRACTION, DEFAULT_VIDEO_WINDOW_MS, REFERENCE_WEBGAZER_LAG_MS, VIDEO_WINDOW_OPTIONS_MS, type DataSource } from "@gazelab/shared";
import { api } from "../../services/api.js";
import { useAsync } from "../../lib/useAsync.js";
import { Alert, ErrorAlert, SyntheticBanner } from "../../components/ui/index.js";
import { NoticeStrip } from "../../components/ui/Layout.js";
import { useStudy } from "./StudyLayout.js";
import { DEMO_EXPORT_NOTE, useDemo } from "./AdminLayout.js";

export function StudyExportPage() {
  const { study } = useStudy();
  const exportsOff = useDemo()?.enabled === true;
  /** Enlace de descarga, o botón inactivo en modo demo. */
  const dl = (href: string, label: string, testId: string, className = "btn-primary") =>
    exportsOff ? (
      <button type="button" className={`${className} cursor-not-allowed opacity-50`} disabled title={DEMO_EXPORT_NOTE} data-testid={testId}>
        {label}
      </button>
    ) : (
      <a className={className} href={href} download data-testid={testId}>
        {label}
      </a>
    );
  const images = useAsync(() => api.listImages(study.id), [study.id]);
  const [source, setSource] = useState<DataSource>(study.sessionCounts.webgazer.completed > 0 || study.sessionCounts.synthetic.total === 0 ? "webgazer" : "synthetic");
  const [imageId, setImageId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const [groupA, setGroupA] = useState(study.groups[0]?.id ?? "");
  const [groupB, setGroupB] = useState(study.groups[1]?.id ?? "");
  const [sigma, setSigma] = useState(DEFAULT_SIGMA_FRACTION);
  const [analysisImage, setAnalysisImage] = useState("");
  const hasVideos = study.videoCount > 0;
  const videos = (images.data ?? []).filter((i) => i.kind === "video" && i.processingStatus === "ready");
  const [windowsVideo, setWindowsVideo] = useState("");
  const [windowMs, setWindowMs] = useState<number>(DEFAULT_VIDEO_WINDOW_MS);
  const [lagMode, setLagMode] = useState<"none" | "measured" | "fixed">("none");
  const [lagMs, setLagMs] = useState<number>(REFERENCE_WEBGAZER_LAG_MS);
  const noun = "Imagen o video";

  useEffect(() => {
    if (!analysisImage && images.data?.length) setAnalysisImage(images.data[0]!.id);
  }, [images.data, analysisImage]);

  const filters = { source, imageId: imageId || undefined, groupId: groupId || undefined, includeExcluded: includeExcluded || undefined };
  const samplesUrl = api.exportUrl(study.id, "samples.csv", filters);
  const summaryUrl = api.exportUrl(study.id, "summary.csv", filters);
  const analysisReady = Boolean(analysisImage && groupA && groupB && groupA !== groupB);
  const analysisUrl = api.exportUrl(study.id, "analysis.json", { source, imageId: analysisImage, groupA, groupB, sigma });
  const windowsVideoId = windowsVideo || videos[0]?.id || "";
  const windowsUrl = api.exportUrl(study.id, "windows.csv", { source, imageId: windowsVideoId, groupId: groupId || undefined, windowMs, lagMode, lagMs });

  return (
    <div className="space-y-5">
      {exportsOff && (
        <NoticeStrip tone="warning" icon="alert" testId="demo-exports-off">
          <strong>Modo demo:</strong> las exportaciones de datos (CSV, JSON y PNG) están deshabilitadas.
        </NoticeStrip>
      )}
      {study.sessionCounts.synthetic.total > 0 && <SyntheticBanner />}
      <ErrorAlert error={images.error} />
      <section className="card space-y-4" aria-labelledby="exp-filters" data-tour="export-filters">
        <h2 id="exp-filters" className="text-lg font-semibold">
          Filtros
        </h2>
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <label htmlFor="exp-source" className="label">
              Fuente (nunca se mezclan)
            </label>
            <select id="exp-source" className="input" value={source} onChange={(e) => setSource(e.target.value as DataSource)}>
              <option value="webgazer">Reales (webcam)</option>
              <option value="synthetic">SINTÉTICA (demo)</option>
            </select>
          </div>
          <div>
            <label htmlFor="exp-image" className="label">
              {noun}
            </label>
            <select id="exp-image" className="input" value={imageId} onChange={(e) => setImageId(e.target.value)}>
              <option value="">Todas</option>
              {(images.data ?? []).map((i) => (
                <option key={i.id} value={i.id}>
                  {i.filenameOriginal}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="exp-group" className="label">
              Grupo
            </label>
            <select id="exp-group" className="input" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Todos</option>
              {study.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <label htmlFor="exp-excl" className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input id="exp-excl" type="checkbox" checked={includeExcluded} onChange={(e) => setIncludeExcluded(e.target.checked)} />
              Incluir sesiones excluidas manualmente
            </label>
          </div>
        </div>
        {source === "synthetic" && (
          <Alert tone="warning">Los archivos se generarán con datos SINTÉTICOS (columna «source» = synthetic y nombre de archivo marcado).</Alert>
        )}
      </section>

      <section className="grid items-stretch gap-4 lg:grid-cols-2 2xl:grid-cols-3" data-tour="export-files">
        <div className="card flex flex-col gap-2">
          <h3 className="font-semibold">samples.csv</h3>
          <p className="text-sm text-slate-600">
            Una fila por muestra estimada de mirada: sesión, grupo, edad, fuente, estímulo, ensayo, tiempo (ms desde el inicio de la captura), coordenadas de pantalla (px CSS),
            coordenadas normalizadas del estímulo (sin redondeo), si cae en él, validez y motivo de invalidez. Incluye ensayos interrumpidos (columna de estado) para trazabilidad.
            {hasVideos && " En videos, además: tiempo del fotograma visible (video_time_ms) y su índice (frame_index)."}
          </p>
          {dl(samplesUrl, "Descargar samples.csv", "export-samples", "btn-primary mt-auto self-start")}
        </div>
        <div className="card flex flex-col gap-2">
          <h3 className="font-semibold">summary.csv</h3>
          <p className="text-sm text-slate-600">
            Una fila por sesión × estímulo: estado de sesión y ensayo, calidad de validación, conteos de muestras (crudas, válidas, en imagen), tasa de muestreo, exclusiones y factores de
            confusión registrados (lentes, visión, dispositivo). También la reproducción de cada video (fotogramas, pérdidas, detenciones) y la validación con punto en movimiento (retraso, error).
          </p>
          {dl(summaryUrl, "Descargar summary.csv", "export-summary", "btn-primary mt-auto self-start")}
        </div>
        <div className="card space-y-3 lg:col-span-2 2xl:col-span-1">
          <h3 className="font-semibold">analysis.json</h3>
          <p className="text-sm text-slate-600">
            Resultado del análisis comparativo de una imagen o un video completo entre dos grupos: matrices de densidad (A, B y diferencia), conteos efectivos, exclusiones, métricas por ROI, parámetros
            (σ, rejilla, filtros) y versión del algoritmo.
          </p>
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <label htmlFor="exp-a-img" className="label">
                {noun}
              </label>
              <select id="exp-a-img" className="input" value={analysisImage} onChange={(e) => setAnalysisImage(e.target.value)}>
                {(images.data ?? []).map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.filenameOriginal}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="exp-ga" className="label">
                Grupo A
              </label>
              <select id="exp-ga" className="input" value={groupA} onChange={(e) => setGroupA(e.target.value)}>
                {study.groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="exp-gb" className="label">
                Grupo B
              </label>
              <select id="exp-gb" className="input" value={groupB} onChange={(e) => setGroupB(e.target.value)}>
                {study.groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="exp-sigma" className="label">
                σ (fracción del lado largo)
              </label>
              <input id="exp-sigma" type="number" className="input" min={0.005} max={0.2} step={0.005} value={sigma} onChange={(e) => setSigma(e.target.valueAsNumber || DEFAULT_SIGMA_FRACTION)} />
            </div>
          </div>
          {analysisReady ? (
            dl(analysisUrl, "Descargar analysis.json", "export-analysis")
          ) : (
            <Alert tone="info">Elige una imagen o un video y dos grupos distintos.</Alert>
          )}
          <p className="text-xs text-slate-500">Los PNG de mapas de calor se exportan desde la pestaña «Resultados», con leyenda y metadatos incrustados.</p>
        </div>
        {videos.length > 0 && (
          <div className="card space-y-3 lg:col-span-2 2xl:col-span-3" data-testid="export-windows-card">
            <h3 className="font-semibold">windows.csv</h3>
            <p className="text-sm text-slate-600">
              Métricas por ventana de tiempo de un video: una fila por participante elegible × ventana × ROI, con las muestras en la ventana y la fracción dentro de cada
              ROI (vacía fuera de su intervalo). Mismas reglas de elegibilidad que el análisis; respeta el filtro de grupo.
            </p>
            <div className="grid gap-3 md:grid-cols-4">
              <div>
                <label htmlFor="exp-win-video" className="label">
                  Video
                </label>
                <select id="exp-win-video" className="input" value={windowsVideoId} onChange={(e) => setWindowsVideo(e.target.value)}>
                  {videos.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.filenameOriginal}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="exp-win" className="label">
                  Ventana
                </label>
                <select id="exp-win" className="input" value={windowMs} onChange={(e) => setWindowMs(Number(e.target.value))}>
                  {VIDEO_WINDOW_OPTIONS_MS.map((w) => (
                    <option key={w} value={w}>
                      {w / 1000} s
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="exp-lag" className="label">
                  Corrección de latencia
                </label>
                <select id="exp-lag" className="input" value={lagMode} onChange={(e) => setLagMode(e.target.value as typeof lagMode)}>
                  <option value="none">Sin corrección</option>
                  <option value="measured">Medida por participante (punto en movimiento)</option>
                  <option value="fixed">Fija</option>
                </select>
              </div>
              <div>
                <label htmlFor="exp-lagms" className="label">
                  Retraso fijo / de respaldo (ms)
                </label>
                <input id="exp-lagms" type="number" className="input" min={0} max={2000} step={10} value={lagMs} onChange={(e) => setLagMs(Number.isFinite(e.target.valueAsNumber) ? e.target.valueAsNumber : 0)} disabled={lagMode === "none"} />
              </div>
            </div>
            {dl(windowsUrl, "Descargar windows.csv", "export-windows")}
            <p className="text-xs text-slate-500">«Medida» usa el retraso de cada participante; si no lo tiene, usa el valor fijo (columna lag_source = fixed_fallback).</p>
          </div>
        )}
      </section>
      <NoticeStrip tone="info">
        <strong>Cómo interpretar:</strong> las coordenadas provienen de estimaciones con webcam (ruidosas y dependientes de las condiciones). Compara regiones amplias, reporta siempre n por
        grupo y la calidad de validación, y no presentes diferencias como efectos causales de la edad.
      </NoticeStrip>
    </div>
  );
}
